import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Cell} from 'notebook/tables/constants/CellConstants';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import notifications from 'notifications';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {
  BaseCondition,
  Condition,
  ConditionGroup,
  ConditionValidationState,
  isCondition,
  isConditionGroup,
} from 'studies/typed/shared/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {NEAR_BLACK} from 'ui-library/Colors';
import * as modals from 'util/Modals';
import {Optional} from 'util/Types';

import {buildValidationStatesForUpdate, ConditionDraft} from './conditionUtils';

export function getReplicateCellValue(cell: Cell): number | null {
  if (cell.text == null) {
    return null;
  }
  const value = Number.parseInt(cell.text, 10);
  if (Number.isNaN(value) || value <= 0) {
    notifications.error(
      `The number of replicates was reset to 1 since the specified value was not a whole number greater than 0`,
      {id: 'ReplicateTable-replicate-bounds'}
    );
    return 1;
  }
  if (value > 999) {
    notifications.error(
      `The number of replicates was reset to 999 since the specified value exceeded the maximum`,
      {
        id: 'ReplicateTable-replicate-bounds',
      }
    );
    return 999;
  }
  return value;
}

export function makeValueCell(
  value: number,
  index: number,
  locked: boolean,
  isLockedPerCondition: Array<boolean>
): Cell {
  return {
    text: value.toString(),
    locked: locked || isLockedPerCondition[index],
    align: 'right',
    foregroundColor: NEAR_BLACK,
    bold: false,
  };
}

export function getNameErrorTriangle(
  validationStatePerCondition: Array<ConditionValidationState>,
  index: number,
  procedureType: ProcedureType
): Optional<JSX.Element> {
  if (validationStatePerCondition[index].isNameLengthOverLimit) {
    return (
      <ErrorTriangle
        state={TriangleState.ILLEGAL}
        error={{
          type: '',
          message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
            capital: true,
          })} name may not exceed ${EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH} characters`,
        }}
        location={TriangleLocation.CELL}
      />
    );
  } else if (validationStatePerCondition[index].isDuplicateName) {
    return (
      <ErrorTriangle
        state={TriangleState.ILLEGAL}
        error={{
          type: '',
          message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
            capital: true,
          })} name already in use`,
        }}
        location={TriangleLocation.CELL}
      />
    );
  } else {
    return undefined;
  }
}

export function getDescErrorTriangle(
  validationStatePerCondition: Array<ConditionValidationState>,
  index: number,
  procedureType: ProcedureType
): Optional<JSX.Element> {
  if (validationStatePerCondition[index].isDescLengthOverLimit) {
    return (
      <ErrorTriangle
        state={TriangleState.ILLEGAL}
        error={{
          type: '',
          message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
            capital: true,
          })} description may not exceed ${
            EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH
          } characters`,
        }}
        location={TriangleLocation.CELL}
      />
    );
  } else {
    return undefined;
  }
}

/**
 * Asserts the list is homogeneous (all `Condition` or all `ConditionGroup`) and dispatches to
 * the matching builder. Centralizes the runtime shape check so callers stay generic at the
 * type level and mixed-shape lists fail loudly instead of silently picking a branch.
 *
 * Accepts the wider `BaseCondition` element type so that generic wrappers
 * (`<TCondition extends BaseCondition>`) can pass through without re-narrowing; the runtime
 * assertion is the real safety net regardless of the static type.
 *
 * Empty lists are treated as `Condition`-shaped by convention; both builders produce `[]` from
 * an empty input, so the choice is observationally identical.
 */
export function dispatchByConditionShape<TOut>(
  conditions: ReadonlyArray<BaseCondition>,
  onConditions: (conditions: Array<Condition>) => TOut,
  onGroups: (conditions: Array<ConditionGroup>) => TOut
): TOut {
  if (conditions.length === 0) {
    return onConditions([]);
  }
  const isGroups = isConditionGroup(conditions[0]);
  assert(
    conditions.every((c) => isConditionGroup(c) === isGroups),
    'ConditionGroup and Condition shapes must not be mixed in a single conditions list.'
  );
  return isGroups
    ? onGroups(conditions as unknown as Array<ConditionGroup>)
    : onConditions(conditions as unknown as Array<Condition>);
}

/**
 * Builds {@link Condition}-shaped rows from a `ReplicateTable` edit. Used by the new-run-
 * creation flow, whose conditions are unpersisted-by-construction (no ids); for each new row
 * mapped to an existing row via `originalValueIndexes`, we carry the existing row's optional
 * `id` through so persisted ids survive renames and reorders.
 */
export function buildConditionsFromTableUpdate({
  newConditions,
  originalValueIndexes,
  conditions,
}: {
  newConditions: Array<ConditionDraft>;
  originalValueIndexes: Array<number | null>;
  conditions: Array<Condition>;
}): Array<Condition> {
  const validationStates = buildValidationStatesForUpdate(newConditions);
  return newConditions.map((condition, i) => {
    const originalIndex = originalValueIndexes[i];
    const originalCondition = originalIndex != null ? conditions[originalIndex] : undefined;
    return {
      ...condition,
      id: originalCondition?.id,
      validationState: validationStates[i],
    };
  });
}

/**
 * Builds {@link ConditionGroup}-shaped rows from a `ReplicateTable` edit. Used by the
 * plan and existing-run design tabs; for each new row mapped to an existing row via
 * `originalValueIndexes`, the existing row's `conditionIdsByPmdvId` is carried through so
 * the persisted ↔ display-row linkage survives renames and reorders. Brand-new rows get an
 * empty map, which downstream save serializers consume as "create new backend rows".
 */
export function buildConditionGroupsFromTableUpdate({
  newConditions,
  originalValueIndexes,
  conditions,
}: {
  newConditions: Array<ConditionDraft>;
  originalValueIndexes: Array<number | null>;
  conditions: Array<ConditionGroup>;
}): Array<ConditionGroup> {
  const validationStates = buildValidationStatesForUpdate(newConditions);
  return newConditions.map((condition, i) => {
    const originalIndex = originalValueIndexes[i];
    const originalCondition = originalIndex != null ? conditions[originalIndex] : undefined;
    return {
      ...condition,
      conditionIdsByPmdvId: originalCondition?.conditionIdsByPmdvId ?? {},
      validationState: validationStates[i],
    };
  });
}

export function confirmDeleteConditions({
  unitWord,
  conditionsToRemove,
  allConditions,
  experimentalInputs,
  procedureType,
  isProcedureRunPlan,
  doDelete,
}: {
  unitWord: 'column' | 'row';
  conditionsToRemove: Array<BaseCondition>;
  allConditions: Array<BaseCondition>;
  experimentalInputs: ExperimentalInputs | null;
  procedureType: ProcedureType;
  isProcedureRunPlan: boolean;
  doDelete: () => void;
}): void {
  if (!isProcedureRunPlan) {
    // Persisted-lane detection for runs covers two shapes:
    //  - new-run-modal `Condition` lanes carry an optional `id` (set iff. persisted), and
    //  - legacy-run-design `ConditionGroup` lanes carry a `conditionIdsByPmdvId` map that's
    //    empty iff. unpersisted.
    // Either non-empty marker means the lane is backed by backend rows and must not be deleted
    // client-side; only newly added draft lanes can be removed.
    const persistedConditionsToRemove = conditionsToRemove.filter(
      (condition) =>
        (isCondition(condition) && condition.id != null) ||
        (isConditionGroup(condition) && Object.keys(condition.conditionIdsByPmdvId).length > 0)
    );
    if (persistedConditionsToRemove.length > 0) {
      notifications.error(
        `Cannot remove saved ${getProcedureRunConditionTextForProcedureType(procedureType, {
          plural: persistedConditionsToRemove.length > 1,
        })}.`
      );
      return;
    }
  }

  const numConditions = allConditions.length;
  const numConditionsToRemove = conditionsToRemove.length;
  const singleConditionText = getProcedureRunConditionTextForProcedureType(procedureType);
  const conditionsText = pluralize(numConditionsToRemove, singleConditionText);
  const unitsText = pluralize(numConditionsToRemove, unitWord);
  const numUnitOps = Object.keys(experimentalInputs ?? {}).length;

  modals.confirm(
    `Remove ${conditionsText}?`,
    <>
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text={`${numConditionsToRemove} ${singleConditionText} ${unitsText} across ${numUnitOps} unit ${pluralize(
          numUnitOps,
          'operation'
        )} will be lost`}
      />
      <div className="u-small-spacing-top">
        {`You're about to update your ${`${singleConditionText}s`} from ${numConditions} per unit operation to ${
          numConditions - numConditionsToRemove
        } per unit operation. That means ${numConditionsToRemove} filled ${unitsText} will be deleted.`}
      </div>
    </>,
    ButtonStyle.NEGATIVE,
    {
      acceptText: `Remove ${conditionsText}`,
      rejectText: 'Back to editing',
      enableRedesign: true,
      onSubmit: () => {
        doDelete();
      },
    }
  );
}
